import { c as _c } from "react/compiler-runtime";
import './gift-commitment-card.scss';
import React from 'react';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { ItemCardMeta } from '@sola/ark-core/molecules/item-card/item-card.tsx';
import type { CartDrawerReturnFocusRef } from '@sola/ark-skippy/contexts/cart-drawer.tsx';
import '@sola/locales/formatters.ts';
import type { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isCenterGift } from '@sola/shared-utils/my/gifts-type-guards.ts';
import type { GiftOption } from '../../types/gifts.ts';
import { SponsorshipCard, SponsorshipCardWrapper } from '../sponsorship-card/sponsorship-card.tsx';
import { GiftCommitmentCardAction } from './gift-commitment-card-action.tsx';
export interface GiftCommitmentCardProps {
  commitment?: GraphQLCommitment;
  giftType: GiftTypeEnum;
  giftOption?: GiftOption;
  onGiftOptionsOpen: (returnFocusRef: CartDrawerReturnFocusRef) => void;
  onGiftRemove?: (gift: {
    cartItemId: string;
    childName: string;
    giftType: GiftTypeEnum;
    returnFocusRef: CartDrawerReturnFocusRef;
  }) => Promise<void>;
}

/**
 * Gift commitment card.
 *
 * This component is purely presentational — it renders exactly what `giftOption`
 * describes. For same-center peers, the caller should pass a `giftOption`
 * pre-transformed via `toSameCenterPeerOption()`.
 */
export const GiftCommitmentCard: React.FC<GiftCommitmentCardProps> = t0 => {
  const $ = _c(15);
  const {
    commitment,
    giftType,
    giftOption,
    onGiftOptionsOpen,
    onGiftRemove
  } = t0;
  if (!giftOption || !commitment?.participant) {
    return null;
  }
  const {
    name
  } = commitment.participant;
  const childName = name ?? "";
  let t1;
  if ($[0] !== giftType) {
    t1 = isCenterGift(giftType);
    $[0] = giftType;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const showCenter = t1;
  let t2;
  if ($[2] !== childName || $[3] !== giftType || $[4] !== onGiftRemove) {
    t2 = async (cartItemId, returnFocusRef) => {
      await onGiftRemove?.({
        cartItemId,
        childName,
        giftType,
        returnFocusRef
      });
    };
    $[2] = childName;
    $[3] = giftType;
    $[4] = onGiftRemove;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== childName || $[7] !== giftOption || $[8] !== onGiftOptionsOpen || $[9] !== t2) {
    t3 = <GiftCommitmentCardAction giftOption={giftOption} childName={childName} onGiftOptionsOpen={onGiftOptionsOpen} onGiftRemove={t2} />;
    $[6] = childName;
    $[7] = giftOption;
    $[8] = onGiftOptionsOpen;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== commitment || $[12] !== showCenter || $[13] !== t3) {
    t4 = <SponsorshipCard className="GiftCommitmentCard @container" commitment={commitment} showCenter={showCenter} action={t3} />;
    $[11] = commitment;
    $[12] = showCenter;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export const GiftCommitmentCardSkeleton: React.FC = () => {
  const $ = _c(5);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Skeleton circle={true} height={88} width={88} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Skeleton height={28} width={160} />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex items-center gap-comfy-xs"><Skeleton circle={true} height={20} width={20} /><Skeleton height={20} width={150} /></div>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="ItemCard__titlegroup flex flex-col gap-snug-lg">{t1}{t2}<div className="flex items-center gap-comfy-xs"><Skeleton circle={true} height={20} width={20} /><Skeleton height={20} width={120} /></div></div>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <SponsorshipCardWrapper className="GiftCommitmentCard @container">{t0}{t3}<ItemCardMeta className="SponsorshipCard__action"><Skeleton className="GiftCommitmentCard__skeleton-action" height={54} width={101} /></ItemCardMeta></SponsorshipCardWrapper>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  return t4;
};